import { useCallback, useEffect, useState } from "react";
import { pendingCounts, storageUse, syncAll, type SyncResult } from "./queue";

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

/** Pending counts, storage use, and automatic sync whenever the phone is online. */
export function useSync(surveyUuid?: string, onGps?: (latest: Record<string, unknown> | null) => void) {
  const online = useOnline();
  const [pending, setPending] = useState({ gps: 0, frames: 0, videos: 0, controls: 0, total: 0 });
  const [storage, setStorage] = useState({ usage: 0, quota: 0 });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<SyncResult | null>(null);

  const refresh = useCallback(async () => {
    setPending(await pendingCounts(surveyUuid));
    setStorage(await storageUse());
  }, [surveyUuid]);

  const sync = useCallback(async () => {
    if (!navigator.onLine) return;
    setBusy(true);
    try {
      const r = await syncAll(onGps);
      setLast(r);
    } finally {
      setBusy(false);
      await refresh();
    }
  }, [onGps, refresh]);

  useEffect(() => {
    refresh();
    const t = window.setInterval(async () => {
      await refresh();
      const p = await pendingCounts();
      if (navigator.onLine && p.total && !busy) sync();
    }, 15000);
    return () => window.clearInterval(t);
  }, [refresh, sync, busy]);

  useEffect(() => { if (online) sync(); }, [online]); // eslint-disable-line react-hooks/exhaustive-deps

  return { online, pending, storage, busy, last, sync, refresh };
}

export function mb(bytes: number) {
  return `${(bytes / 1048576).toFixed(bytes > 1073741824 ? 0 : 1)} MB`;
}
